"use client";

import React, { useEffect } from "react";
import { Facebook, Instagram, Linkedin, Loader, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}];
function SolarContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="91185" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-[#39FF14]/20 flex items-center justify-center mb-4 border border-[#39FF14]">
          <div className="size-8 rounded-none bg-[#39FF14] flex items-center justify-center">
            <svg className="size-5 text-[#0A0A0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h3 className="text-2xl font-mono font-bold text-[#F5F5F7] tracking-widest uppercase">PROTOCOL INITIATED</h3>
        <p className="text-[#A1A1AA] font-mono text-sm max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-[#2A2A2E] hover:bg-[#39FF14] hover:text-[#0A0A0B] text-[#F5F5F7] font-mono transition-all">
          RE-SUBMIT DATA
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Principal contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Principal Contact</FormLabel>
              <FormControl>
                <Input placeholder="OPERATOR NAME" className="bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Secure email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid transmission address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Secure Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ADMIN@FACILITY.TLD" className="bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Communication line is required",
        pattern: {
          value: /^[0-9\-\+\s\(\)]+$/,
          message: "Invalid numeric sequence"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Communication Line</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 000 000 0000" className="bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="zip_code" rules={{
        required: "Facility load data is required",
        pattern: {
          value: /^[0-9.]+$/,
          message: "Numeric input required (MW)"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Facility Load (MW)</FormLabel>
              <FormControl>
                <Input placeholder="0.00 MW" className="bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Cooling Architecture <span className="text-[#A1A1AA]/50 text-[10px] font-normal ml-1">(OPTIONAL)</span></FormLabel>
            <FormControl>
              <Input placeholder="LIQUID / AIR / HYBRID" className="bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#39FF14] font-mono text-xs uppercase tracking-wider">Site Specifications <span className="text-[#A1A1AA]/50 text-[10px] font-normal ml-1">(OPTIONAL)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="DESCRIBE THERMAL LOAD PROFILE OR REDUNDANCY REQUIREMENTS..." className="min-h-[120px] bg-transparent border-t-0 border-l-0 border-r-0 border-b border-[#2A2A2E] rounded-none text-[#F5F5F7] placeholder:text-[#A1A1AA]/30 focus-visible:ring-0 focus-visible:border-[#39FF14] transition-all resize-none font-mono text-sm" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/50 rounded-none">
          <p className="text-destructive font-mono text-xs uppercase tracking-tighter">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-mono font-bold uppercase tracking-[0.2em] bg-[#39FF14] text-[#0A0A0B] hover:bg-[#39FF14]/90 rounded-none shadow-none transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            CALIBRATING...
          </> : 'EXECUTE PROTOCOL'}
      </Button>
    </Form>;
}
export default function StartYourSolarJourney() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="audit-protocol" className="dark bg-[#0A0A0B] text-[#F5F5F7] relative overflow-hidden py-24 lg:py-32">
      <Toaster />
      
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#2A2A2E]/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#39FF14]/5 rounded-full blur-[120px] pointer-events-none opacity-40" />
      <div className="absolute left-0 bottom-0 w-[400px] h-[400px] bg-[#39FF14]/5 rounded-full blur-[100px] pointer-events-none opacity-20" />

      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-none border border-[#39FF14]/30 bg-[#39FF14]/5 px-4 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-[#39FF14] backdrop-blur-sm">
                <span className="flex size-1.5 rounded-none bg-[#39FF14] mr-2 animate-pulse"></span>
                SYSTEM STATUS: READY
              </div>
              <h2 className="text-4xl md:text-5xl font-sans font-black tracking-tight text-[#F5F5F7] uppercase leading-[1.1]">
                INITIATE <span className="text-[#39FF14]">AUDIT PROTOCOL</span>
              </h2>
              <p className="text-base text-[#A1A1AA] font-mono leading-relaxed max-w-md">
                Deployment of autonomic AI-thermal intelligence begins with a comprehensive facility assessment. Submit site parameters for exergy recovery analysis.
              </p>
            </div>

            <div className="space-y-6 pt-4 border-t border-[#2A2A2E]">
              <div className="flex items-start space-x-4">
                <div className="mt-1 bg-[#2A2A2E]/30 p-3 rounded-none border border-[#2A2A2E]">
                  <MapPin className="size-5 text-[#39FF14]" />
                </div>
                <div>
                  <h4 className="text-[#F5F5F7] font-mono font-bold text-sm uppercase tracking-wider">DEPLOYMENT REGION</h4>
                  <p className="text-[#A1A1AA] text-xs font-mono mt-1">Providing technical exergy recovery solutions for global C&I and Data Center infrastructure.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="mt-1 bg-[#2A2A2E]/30 p-3 rounded-none border border-[#2A2A2E]">
                  <Mail className="size-5 text-[#39FF14]" />
                </div>
                <div>
                  <h4 className="text-[#F5F5F7] font-mono font-bold text-sm uppercase tracking-wider">ENGINEERING CHANNEL</h4>
                  <p className="text-[#A1A1AA] text-xs font-mono mt-1">Submit technical inquiries via the secure protocol. Response latency typically &lt; 24 hours.</p>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <p className="text-[10px] font-mono font-bold text-[#A1A1AA] mb-4 uppercase tracking-[0.3em]">SECURE DATA NODES</p>
              <div className="flex space-x-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.label} data-index={i} to={social.href} className="bg-transparent hover:bg-[#39FF14] hover:text-[#0A0A0B] border border-[#2A2A2E] p-3 rounded-none transition-all duration-200 text-[#A1A1AA] hover:border-[#39FF14]" aria-label={social.label}>
                      <Icon className="size-4" />
                    </Link>;
              })}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-8">
            <Card className="bg-[#0A0A0B] rounded-none border-[#39FF14] border-[1px] shadow-none overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#39FF14]/5 to-transparent pointer-events-none" />
              <CardHeader className="pb-6 border-b border-[#2A2A2E] relative z-10">
                <CardTitle className="text-xl font-mono font-bold text-[#F5F5F7] tracking-wider uppercase">EXERGY AUDIT REQUEST</CardTitle>
                <CardDescription className="text-[#A1A1AA] text-xs font-mono tracking-tight leading-relaxed">
                  Input facility parameters to calibrate autonomic AI-thermal intelligence. All data handled via secure engineering protocols.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8 relative z-10">
                <FormProvider formKey="audit_protocol_form_exergy_audit" formId="audit_protocol_form_exergy_audit" sectionName="Start Your Solar Journey" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                address: "",
                zip_code: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                zip_code: {
                  required: true,
                  pattern: "^[0-9.]+$"
                }
              }} submitText="EXECUTE PROTOCOL" successMessage="PROTOCOL INITIATED. Data packets received. Our engineering team will transmit the preliminary exergy report within 24 hours.">
                  <SolarContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}